import { useMemo, useState, useEffect, useCallback } from "react";
import messages from "messages";
import { Button, FormControl, ListGroupItem } from "react-bootstrap";
// hooks
import { useTreeWidgetContext } from "../../contexts/TreeWidgetContext";
// components
import { ReactComponent as OutlineArrowLeft } from "assets/svg/outline-arrow-left.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as OutlineFolderCreate } from "assets/svg/outline-folder-create.svg";
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right.svg";
import IconButton from "components/icon-button";
import TypeIcon from "../../../type-icon";
// styles
import styles from "./styles.module.scss";
// types
import type { BatchEntryOpsFunc } from "contexts/orchid-drive/components/orchidDriveProvider";
import type {
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
import type { TDataInView } from "components/Dashboard/DoctorDashboard/hooks/useOrchidDriveDataInView";
// utils
import getMIMETypeByFileName from "utils/getMIMETypeByFileName";

type Props = {
  dataInView: TDataInView;
  batchEntryOps: BatchEntryOpsFunc;
  onCreateDir: (
    name: string,
    parentDirectoryId: string | undefined | null,
  ) => Promise<OrchidDriveDirectory>;
};

/**
 * renders a widget on bottom left to show the drive data structure
 */
const TreeWidgetUI = ({ dataInView, batchEntryOps, onCreateDir }: Props) => {
  const {
    show,
    close,
    originFolder,
    folderPaths,
    setFolderPaths,
    selected,
    onMoveEnd,
  } = useTreeWidgetContext();

  const hasData = useMemo(
    () => dataInView.filesInView.length + dataInView.foldersInView.length > 0,
    [dataInView.filesInView.length, dataInView.foldersInView.length],
  );

  /**
   * when null, meaning it is in root folder
   * when undefined, meaning it is not in root folder, and has no folder selected
   */
  const [targetFolder, setTargetFolder] = useState<
    OrchidDriveDirectory | null | undefined
  >();

  /**
   * on change directory, reset target
   */
  useEffect(() => {
    setTargetFolder(folderPaths[folderPaths.length - 1]);
  }, [folderPaths]);

  // reset dest folder
  const handleClose = useCallback(() => {
    setTargetFolder(folderPaths[folderPaths.length - 1]);
    close();
  }, [close, folderPaths]);

  const handleMove = useCallback(async () => {
    try {
      await batchEntryOps({
        entryMoves: selected.map((el) => ({
          entryId: el.guid,
          parentDirectoryId: targetFolder?.directoryId || null,
        })),
        entryDeletes: [],
      });

      // close the move to widget
      handleClose();

      // reset the selected items and show message
      return (
        onMoveEnd &&
        onMoveEnd({
          selected,
          originFolder: originFolder || { name: messages.attachments },
          targetFolder: targetFolder || { name: messages.attachments },
          success: true,
        })
      );
    } catch (error) {
      // reset the selected items and show message
      return (
        onMoveEnd &&
        onMoveEnd({
          selected,
          originFolder: originFolder || { name: messages.attachments },
          targetFolder: targetFolder || { name: messages.attachments },
          success: false,
        })
      );
    }
  }, [
    batchEntryOps,
    handleClose,
    onMoveEnd,
    originFolder,
    selected,
    targetFolder,
  ]);

  const [creatingFolder, setCreatingFolder] = useState<{
    parent?: OrchidDriveDirectory | undefined;
    name: string;
    busy?: boolean;
  }>();

  const isSameFolder = useMemo(
    () => targetFolder?.directoryId === originFolder?.directoryId,
    [originFolder?.directoryId, targetFolder?.directoryId],
  );

  return !show ? null : (
    <div
      className={styles["tree-widget-ui"]}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* header */}
      <div className={styles["tree-widget-ui__header"]}>
        {/* back button */}
        {(folderPaths.length > 0 || creatingFolder) && (
          <IconButton
            className={
              styles["tree-widget-ui__back"] +
              " " +
              styles["tree-widget-ui__icon"]
            }
            disabled={creatingFolder ? false : !folderPaths.length}
            onClick={() =>
              creatingFolder
                ? setCreatingFolder(undefined)
                : setFolderPaths((v) => v.slice(0, v.length - 1))
            }
          >
            <OutlineArrowLeft />
          </IconButton>
        )}
        {/* name */}
        {creatingFolder ? (
          <FormControl
            className={
              styles["tree-widget-ui__title"] +
              " " +
              styles["tree-widget-ui__title--input"]
            }
            value={creatingFolder.name}
            placeholder={messages.untitledFolder}
            onChange={(e) =>
              setCreatingFolder({ ...creatingFolder, name: e.target.value })
            }
          />
        ) : (
          <span className={styles["tree-widget-ui__title"]}>
            {!folderPaths.length
              ? messages.attachments
              : folderPaths[folderPaths.length - 1].name}
          </span>
        )}
        {/* close button */}
        {creatingFolder ? (
          <Button
            variant={"link"}
            className={
              styles["tree-widget-ui__icon"] +
              " " +
              styles["tree-widget-ui__icon--save"]
            }
            disabled={!creatingFolder.name || creatingFolder.busy}
            onClick={() => {
              setCreatingFolder((v) => ({ ...v, busy: true }));
              onCreateDir(
                creatingFolder.name,
                folderPaths[folderPaths.length - 1]?.directoryId,
              )
                .then((createdEntry) => {
                  setFolderPaths((v) => [...v, createdEntry]);
                  setCreatingFolder(undefined);
                })
                .catch(() => setCreatingFolder((v) => ({ ...v, busy: false })));
            }}
          >
            {messages.save}
          </Button>
        ) : (
          <IconButton
            className={
              styles["tree-widget-ui__icon"] +
              " " +
              styles["tree-widget-ui__close"]
            }
            onClick={handleClose}
          >
            <OutlineCross />
          </IconButton>
        )}
      </div>
      <div className={styles["tree-widget-ui__body"]}>
        {!hasData || creatingFolder ? (
          <ListGroupItem
            className={
              styles["tree-widget-ui__item"] +
              " " +
              styles["tree-widget-ui__item--empty"]
            }
            disabled={true}
          >
            {creatingFolder
              ? `${messages.createAFolderIn} ${
                  creatingFolder.parent?.name || messages.attachments
                }`
              : messages.thisFolderIsEmpty}
          </ListGroupItem>
        ) : (
          (
            [
              [dataInView.filesInView, false],
              [dataInView.foldersInView, true],
            ] as const
          ).map(([datum, isFolder]) =>
            datum.map((el: OrchidDriveDirectory | OrchidDriveFile) => {
              const id = isFolder
                ? (el as OrchidDriveDirectory).directoryId
                : (el as OrchidDriveFile).fileDataId;
              return (
                <ListGroupItem
                  className={styles["tree-widget-ui__item"]}
                  key={id}
                  action={true}
                  disabled={
                    !isFolder ||
                    (isFolder && selected.some((el) => el.guid === id))
                  }
                  {...(isFolder && {
                    active:
                      targetFolder?.directoryId ===
                      (el as OrchidDriveDirectory).directoryId,

                    onClick: () => setTargetFolder(el as OrchidDriveDirectory),
                    onDoubleClick: () =>
                      setFolderPaths((v) => [...v, el as OrchidDriveDirectory]),
                  })}
                >
                  <div className={styles["tree-widget-ui__icon"]}>
                    <TypeIcon
                      mimeType={
                        isFolder
                          ? "folder"
                          : (el as OrchidDriveFile).mimeType ||
                            getMIMETypeByFileName(el.name)
                      }
                    />
                  </div>
                  <div className={styles["tree-widget-ui__item-title"]}>
                    {el.name}
                  </div>

                  {isFolder && (
                    <OutlineChevronRight
                      className={styles["tree-widget-ui__icon"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFolderPaths((v) => [
                          ...v,
                          el as OrchidDriveDirectory,
                        ]);
                      }}
                    />
                  )}
                </ListGroupItem>
              );
            }),
          )
        )}
      </div>

      {/* footer */}
      <div className={styles["tree-widget-ui__footer"]}>
        {/* TODO: remove disabled when function is complete */}
        <IconButton
          disabled={!!creatingFolder}
          onClick={() =>
            setCreatingFolder({
              parent: folderPaths[folderPaths.length - 1],
              name: messages.untitledFolder,
            })
          }
        >
          <OutlineFolderCreate />
        </IconButton>

        <Button
          onClick={handleMove}
          disabled={!!creatingFolder || isSameFolder}
        >
          {messages.moveHere}
        </Button>
      </div>
    </div>
  );
};

export default TreeWidgetUI;
